/**
 * #987 — does this login have a Polymarket US account it can view as its own?
 *
 * One `GET /api/polymarket-us/accounts` per login epoch, shared by every
 * caller (the Recent Fills rail, the board), so a surface can tell "US is
 * failing for a US trader" from "this trader has no US" without waiting for
 * its own US read to succeed once. `null` = not known yet or the read failed;
 * `useUsAccountPresenceState` also says which.
 *
 * A failed read is not "no US". It is read again on the side rail's cadence
 * (every 10 s, none while the page is hidden, one as soon as it is visible
 * again) until it answers, so one failure at sign-in (a relay restart, a
 * database blip) never hides US for the rest of the session. Every caller's
 * retry lands on the same due time, so the page sends one read per retry, not
 * one per caller. A new caller still reads at once, as before.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { createUsBrowserAccountClient } from '@/lib/polymarketUs/browserAccountClient';
import { getAuthSessionEpoch, useAuthStore } from '@/stores/authStore';

/** Same cadence as the side rail's US reads. */
export const US_PRESENCE_RETRY_MS = 10_000;

/** The first account this login views as its own, as the list read gave it
 * (R4-F1: the US dot in the chrome reads its readiness, once, from the same list). */
export type UsFirstOwnAccount = { accountId: string; accountEnabled: boolean };
type Entry = { epoch: number; read: Promise<boolean>; value?: boolean; first?: UsFirstOwnAccount | null };
/** `value`: the answer once the read has given one, so a caller mounted later
 * (the board pop-up opening again) starts from it rather than from "unknown". */
let cache: Entry | null = null;
/** When the next read after a failure is due, on the page's monotonic clock. */
let retryDueAt = 0;

function readPresence(epoch: number): Promise<boolean> {
  if (cache?.epoch === epoch) return cache.read;
  const entry: Entry = { epoch, read: Promise.resolve(false) };
  const read = createUsBrowserAccountClient()
    .listAccounts()
    .then((page) => {
      const own = page.accounts.find((a) => a.canViewOwn);
      entry.first = own ? { accountId: own.accountId, accountEnabled: own.accountEnabled === true } : null;
      return own !== undefined;
    });
  entry.read = read;
  cache = entry;
  // Registered before any caller's handler, so every caller sees the new due time.
  read.then(
    (value) => { entry.value = value; },
    () => {
      if (cache === entry) cache = null;
      retryDueAt = performance.now() + US_PRESENCE_RETRY_MS;
    },
  );
  return read;
}

/** This login's answer, when a read has already given one. */
const cachedPresence = (epoch: number): boolean | undefined =>
  (cache?.epoch === epoch ? cache.value : undefined);
/** The first own-view account of this login (null: none) once the list read has answered; undefined before. */
export const cachedUsFirstOwnAccount = (epoch: number): UsFirstOwnAccount | null | undefined =>
  (cache?.epoch === epoch ? cache.first : undefined);

/** Test seam: forget the cached answer and any pending retry time. */
export function resetUsAccountPresence(): void {
  cache = null;
  retryDueAt = 0;
}

const subscribe = (notify: () => void) => useAuthStore.subscribe(notify);
const codeOf = (error: unknown) => error && typeof error === 'object' && 'code' in error
  && typeof error.code === 'string' ? error.code : 'US_READ_UNAVAILABLE';

export type UsAccountPresence = {
  /** true / false once a read answered for this login; null until then. */
  value: boolean | null;
  /** The last read for this login failed and no read has answered since. */
  failed: boolean;
  /** Code of that failure; null when not failed. */
  error: string | null;
};

const UNKNOWN: UsAccountPresence = { value: null, failed: false, error: null };

export function useUsAccountPresenceState(enabled = true): UsAccountPresence {
  const epoch = useSyncExternalStore(subscribe, getAuthSessionEpoch);
  const authenticated = useAuthStore((s) => s.status === 'authenticated');
  const [state, setState] = useState<{ epoch: number; value: boolean } | { epoch: number; error: string } | null>(null);
  // Bumped to read again after a failure.
  const [attempt, setAttempt] = useState(0);
  // An answer already in hand for this login is used from the first render, so
  // a surface mounted later never shows an "unknown" step before it.
  const cached = authenticated ? cachedPresence(epoch) : undefined;
  const own = state?.epoch === epoch ? state : null;
  const mine = own && 'value' in own ? own : cached !== undefined ? { epoch, value: cached } : own;
  const known = mine !== null && 'value' in mine;
  useEffect(() => {
    if (!enabled || !authenticated || known) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const due = () => performance.now() >= retryDueAt;
    const onVisible = () => {
      if (cancelled || document.hidden) return;
      document.removeEventListener('visibilitychange', onVisible);
      if (due()) setAttempt((n) => n + 1);
      else timer = setTimeout(fire, retryDueAt - performance.now());
    };
    function fire() {
      if (cancelled) return;
      if (document.hidden) {
        document.addEventListener('visibilitychange', onVisible);
        return;
      }
      // Another caller's later failure moved the due time: wait for it.
      if (!due()) {
        timer = setTimeout(fire, retryDueAt - performance.now());
        return;
      }
      setAttempt((n) => n + 1);
    }
    readPresence(epoch).then(
      (value) => {
        if (!cancelled) setState({ epoch, value });
      },
      (error: unknown) => {
        if (cancelled) return;
        const code = codeOf(error);
        setState((s) => (s?.epoch === epoch && 'error' in s && s.error === code ? s : { epoch, error: code }));
        timer = setTimeout(fire, Math.max(0, retryDueAt - performance.now()));
      },
    );
    return () => {
      cancelled = true;
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, authenticated, known, epoch, attempt]);
  if (!mine) return UNKNOWN;
  if ('value' in mine) return { value: mine.value, failed: false, error: null };
  // A caller that stopped reading is not told about a retry it no longer runs.
  return enabled && authenticated ? { value: null, failed: true, error: mine.error } : UNKNOWN;
}

export function useUsAccountPresence(enabled = true): boolean | null {
  return useUsAccountPresenceState(enabled).value;
}
